import type {HistoryMaterialStorage} from '@domain/material-storage'
import {type ScheduleItem} from '@domain/schedule'
import {AssetSnapshot, AssetType} from '@hconnect/common/types'
import {roundTo15MinIntervalStart} from '@hconnect/common/utils'
import {dataTestId} from '@hconnect/uikit'
import {CardSectionTitle} from '@hconnect/uikit/src/lib2'
import {useUpmPlantTimezone} from '@hconnect/upm'
import {Stack} from '@mui/material'
import {useCurrentTimeRounded} from '@widgets/stock-card'
import moment, {Moment} from 'moment-timezone'
import {useTranslation} from 'react-i18next'

import {FEATURE_NAMESPACE} from '../../../../consts'
import {PlannerSwitch} from '../../formComponents'

import {useAttachedStorages} from './hooks'
import {RemainingStoragesSelect} from './RemainingStoragesSelect'
import {SelectStorages} from './SelectStorages'
import {SelectStoragesOrdered} from './SelectStoragesOrdered'

interface SelectStorageSectionProps {
  asset: AssetSnapshot
  scheduleItem: ScheduleItem
  startOfPlan: Moment
  endOfPlan: Moment
  availableStorages: HistoryMaterialStorage[]
  storagesById: Record<string, HistoryMaterialStorage>
  updateScheduleItem?: (item: ScheduleItem) => void
}

export const SelectStorageSection: React.FC<SelectStorageSectionProps> = ({
  asset,
  scheduleItem,
  startOfPlan,
  endOfPlan,
  availableStorages,
  storagesById,
  updateScheduleItem
}) => {
  const {t} = useTranslation(FEATURE_NAMESPACE)
  const timezoneId = useUpmPlantTimezone()
  const nowRounded = useCurrentTimeRounded({timezoneId, roundingFn: roundTo15MinIntervalStart})

  const isSiloNominationDisabled = moment.utc(scheduleItem.end).isBefore(nowRounded)

  const {
    selectedSilos: {isOrderSpecified, ids: selectedSilosIds}
  } = scheduleItem

  const handleToggleSpecifyOrder = () => {
    updateScheduleItem?.({
      ...scheduleItem,
      selectedSilos: {...scheduleItem.selectedSilos, isOrderSpecified: !isOrderSpecified}
    })
  }

  const {
    selectedStorages,
    selectedStoragesOrdered,
    selectedAvailableStoragesIds,
    selectedRemainingStoragesIds,
    remainingStorages
  } = useAttachedStorages({
    storagesById,
    availableStorages,
    selectedSilosIds
  })

  const shouldRemainingStoragesSelectBeShown =
    !isSiloNominationDisabled && asset.type === AssetType.CementMill

  return (
    <Stack spacing={1.5} {...dataTestId('select_storage_section')}>
      <Stack direction="row" justifyContent="space-between" alignItems="center">
        <CardSectionTitle>{t('planning.produceTo')}</CardSectionTitle>
        <PlannerSwitch
          label={t('planning.specifyOrder')}
          value={isOrderSpecified}
          onChange={handleToggleSpecifyOrder}
          disabled={isSiloNominationDisabled}
          {...dataTestId('specify_order_switch')}
        />
      </Stack>
      {isOrderSpecified ? (
        <SelectStoragesOrdered
          selectedStorages={selectedStoragesOrdered}
          scheduleItem={scheduleItem}
          isReadOnly={isSiloNominationDisabled}
          updateScheduleItem={updateScheduleItem}
        />
      ) : (
        <SelectStorages
          selectedStorages={selectedStorages}
          scheduleItem={scheduleItem}
          startOfPlan={startOfPlan}
          endOfPlan={endOfPlan}
          isReadOnly={isSiloNominationDisabled}
          updateScheduleItem={updateScheduleItem}
        />
      )}
      {shouldRemainingStoragesSelectBeShown && updateScheduleItem && (
        <RemainingStoragesSelect
          selectedAvailableStoragesIds={selectedAvailableStoragesIds}
          selectedRemainingStoragesIds={selectedRemainingStoragesIds}
          remainingStorages={remainingStorages}
          scheduleItem={scheduleItem}
          updateScheduleItem={updateScheduleItem}
        />
      )}
    </Stack>
  )
}
